// =====================================================================================
// INVRT Radar - web console - the operator Organisation Dossier (R5, 7 October 2026)
//
// One read-mostly screen per organisation on the operator console (the owner's ruling,
// PRD_RESELLER_RESET_2026-10-08.md R5): identity, plan (module + campaign
// subscriptions), credits and ledger, usage, members, sites, API keys, campaign
// bindings, and the audit history. Opened from the Dossier action on the Organisations
// list (/operator/organisations/:orgId).
//
// The money tab is the R8 ruling: this screen renders the SAME Postgres figures the
// tenant's own Account screen renders - the ledger_balance SUM, the verbatim ledger
// rows, and the same ledger_usage_summary groups - through the house formatters. It is
// read-only end to end: the only request this screen ever makes is the one GET.
//
// The API carries no supplier identifiers in any dossier field (api keys and sites are
// metadata only, usage rows carry no provider column), so a supplier name can no more
// reach this screen than it can the tenant's.
// =====================================================================================

import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Badge } from "@/components/ui/badge";
import { buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Chip } from "@/components/ui/chip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { useSession } from "@/auth/SessionProvider";
import { useApiClient } from "@/hooks/useApi";
import { isApiError } from "@/lib/api";
import { formatCredits, formatCreditsWithUnit, formatDateTime, humaniseAction } from "@/lib/format";
import { summarise, scopeValid, type Scope } from "@/pages/operator/campaignScope";

// ---- the route contract (GET /v1/admin/organisations/:org_id/dossier) ------------------

type Numeric = number | string;

interface DossierOrganisation {
  id: string;
  name: string;
  slug: string;
  org_kind: "tenant" | "reseller" | string;
  parent_org_id: string | null;
  status: string;
  tenant_class: string;
  daily_cap_credits: Numeric | null;
  region: string | null;
  created_at: string;
  updated_at: string;
}

interface DossierMember {
  user_id: string;
  email: string;
  full_name: string | null;
  created_at: string;
}

interface DossierModuleSubscription {
  id: string;
  module_code: string;
  module_name: string;
  status: string;
  effective_from: string;
  effective_to: string | null;
  created_at: string;
}

interface DossierCampaignSubscription {
  id: string;
  module_code: string;
  module_name: string;
  campaign_code: string;
  campaign_name: string;
  status: string;
  effective_from: string;
  effective_to: string | null;
  created_at: string;
}

interface DossierLedgerRow {
  id: number | string;
  entry_type: string;
  credits_delta: Numeric;
  action: string | null;
  memo: string | null;
  created_at: string;
}

interface DossierUsageGroup {
  action: string | null;
  entry_type: string;
  entry_count: number;
  credits_delta_total: Numeric;
  unit_count_total: Numeric | null;
}

interface DossierSite {
  id: string;
  domain: string;
  pixel_status: string;
  trial_state: string | null;
  created_at: string;
}

interface DossierApiKey {
  id: string;
  label: string | null;
  key_prefix: string;
  status: string;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

interface DossierBinding {
  campaign_subscription_id: string;
  sync_state: string;
  requested_geo_scope: unknown;
  last_verified_geo_scope: unknown;
  last_verified_at: string | null;
}

interface DossierAuditRow {
  id: number | string;
  action: string;
  actor_type: string | null;
  actor_id: string | null;
  subject_type: string | null;
  subject_id: string | null;
  created_at: string;
}

export interface OrganisationDossierResponse {
  organisation: DossierOrganisation;
  members: DossierMember[];
  module_subscriptions: DossierModuleSubscription[];
  campaign_subscriptions: DossierCampaignSubscription[];
  credits: {
    balance: Numeric;
    ledger: { rows: DossierLedgerRow[]; limit: number };
  };
  usage: { window: { from: string; to: string }; by_action: DossierUsageGroup[] };
  sites: DossierSite[];
  api_keys: DossierApiKey[];
  campaign_bindings: DossierBinding[];
  audit: { rows: DossierAuditRow[]; limit: number };
  correlation_id: string;
}

// ---- rendering ------------------------------------------------------------------------

function statusSignal(status: string): "buy" | "risk" | undefined {
  if (status === "active") return "buy";
  if (status === "suspended") return "risk";
  return undefined;
}

function scopeSummary(scope: unknown): string {
  return scopeValid(scope) ? summarise(scope as Scope) : "Unreadable scope";
}

// The dossier API's per-section page caps (api/routes/organisations_dossier.ts): the
// newest-first lists cap at 101 rows, while the ledger and the audit cap at the limit
// each answer with inside the payload. A section that returns exactly its cap says so,
// so an operator never reads a bounded window as if it were the whole history.
const DOSSIER_SECTION_CAP = 101;

function boundHint(
  count: number,
  limit: number,
  ordering: "newest" | "oldest" | "first",
  noun: string,
): string | null {
  if (limit === 0 || count !== limit) return null;
  return `Showing the ${ordering} ${limit} ${noun} only.`;
}

function BoundHint({ text }: { text: string | null }) {
  if (text === null) return null;
  return <p className="mt-3 text-xs text-ink-muted">{text}</p>;
}

function SectionCard({
  title,
  blurb,
  badge,
  children,
}: {
  title: string;
  blurb?: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle>{title}</CardTitle>
          {blurb ? <p className="mt-1 text-sm text-ink-muted">{blurb}</p> : null}
        </div>
        {badge}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export function OperatorDossierScreen() {
  const { isSuperAdmin } = useSession();
  const { orgId = "" } = useParams<{ orgId: string }>();
  const api = useApiClient();

  const dossier = useQuery({
    queryKey: ["operator", "dossier", orgId],
    queryFn: () => api.get<OrganisationDossierResponse>(`/admin/organisations/${orgId}/dossier`),
    enabled: isSuperAdmin && Boolean(orgId),
  });

  if (!isSuperAdmin) {
    return (
      <EmptyState
        title="The dossier is for INVRT platform operators"
        body="This area is for INVRT platform operators. The server also refuses requests without super-admin access."
      />
    );
  }
  if (dossier.isLoading) return <LoadingState label="Loading the dossier…" />;
  if (dossier.isError) {
    if (isApiError(dossier.error) && dossier.error.httpStatus === 404) {
      return (
        <EmptyState
          title="The organisation is unknown."
          body="It may have been closed, or the link is out of date. Return to the organisations list."
          tone="error"
          action={
            <Link to="/operator/organisations" className={buttonVariants({ variant: "secondary", size: "sm" })}>
              Back to organisations
            </Link>
          }
        />
      );
    }
    return <ErrorState error={dossier.error} onRetry={() => void dossier.refetch()} />;
  }
  if (!dossier.data) return null;
  const data = dossier.data;
  const org = data.organisation;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/operator/organisations"
            className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
            Back to organisations
          </Link>
          <h1 className="mt-2 text-xl font-semibold text-ink">{org.name}</h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink-muted">
            <Chip signal={statusSignal(org.status)} dot={org.status === "active"}>
              {org.status}
            </Chip>
            <span>{org.org_kind === "reseller" ? "Reseller" : "Tenant"}</span>
            <span aria-hidden="true">·</span>
            <span>Created {formatDateTime(org.created_at)}</span>
          </p>
        </div>
        <Badge tone="success">Balance {formatCreditsWithUnit(data.credits.balance)}</Badge>
      </header>

      <SectionCard
        title="Identity"
        blurb="What this organisation is, where it sits in the estate, and its spend ceiling."
      >
        <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-ink-muted">Kind</dt>
            <dd className="text-sm text-ink">{org.org_kind === "reseller" ? "Reseller" : "Tenant"}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-ink-muted">Parent organisation</dt>
            <dd className="text-sm text-ink">
              {org.parent_org_id ? <span className="font-mono text-xs">{org.parent_org_id}</span> : "No parent organisation"}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-ink-muted">Tenant class</dt>
            <dd className="text-sm text-ink">{humaniseAction(org.tenant_class)}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-ink-muted">Daily credit cap</dt>
            <dd className="text-sm text-ink">
              {org.daily_cap_credits === null ? "Uncapped" : `${formatCredits(org.daily_cap_credits)} credits per day`}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-ink-muted">Region</dt>
            <dd className="text-sm text-ink">{org.region ?? "None recorded"}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-ink-muted">Last updated</dt>
            <dd className="text-sm text-ink">{formatDateTime(org.updated_at)}</dd>
          </div>
        </dl>
      </SectionCard>

      <SectionCard title="Members" blurb="The people who hold a membership in this organisation.">
        {data.members.length === 0 ? (
          <EmptyState title="No members yet" body="Invitations sent from the organisations list appear here once accepted." />
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Person</TableHeaderCell>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Joined</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.members.map((member) => (
                <TableRow key={member.user_id}>
                  <TableCell className="font-medium">{member.full_name ?? "Name not recorded"}</TableCell>
                  <TableCell className="text-ink-muted">{member.email}</TableCell>
                  <TableCell className="whitespace-nowrap text-ink-muted">{formatDateTime(member.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <BoundHint text={boundHint(data.members.length, DOSSIER_SECTION_CAP, "newest", "members")} />
      </SectionCard>

      <SectionCard title="Plan" blurb="The modules and campaigns this organisation is subscribed to.">
        <div className="flex flex-col gap-4">
          {data.module_subscriptions.length === 0 ? (
            <EmptyState title="No module subscriptions yet" body="Assign a module subscription from the access screen." />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Module</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Effective from</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.module_subscriptions.map((subscription) => (
                  <TableRow key={subscription.id}>
                    <TableCell className="font-medium">
                      {subscription.module_name}
                      <span className="ml-2 text-xs text-ink-muted">({subscription.module_code})</span>
                    </TableCell>
                    <TableCell>
                      <Chip signal={statusSignal(subscription.status)} dot={subscription.status === "active"}>
                        {subscription.status}
                      </Chip>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-ink-muted">{formatDateTime(subscription.effective_from)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <BoundHint
            text={boundHint(data.module_subscriptions.length, DOSSIER_SECTION_CAP, "newest", "module subscriptions")}
          />
          {data.campaign_subscriptions.length === 0 ? (
            <EmptyState title="No campaign subscriptions yet" body="Assign a campaign from the access screen." />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Campaign</TableHeaderCell>
                  <TableHeaderCell>Module</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Effective from</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.campaign_subscriptions.map((subscription) => (
                  <TableRow key={subscription.id}>
                    <TableCell className="font-medium">
                      {subscription.campaign_name}
                      <span className="ml-2 text-xs text-ink-muted">({subscription.campaign_code})</span>
                    </TableCell>
                    <TableCell className="text-ink-muted">{subscription.module_name}</TableCell>
                    <TableCell>
                      <Chip signal={statusSignal(subscription.status)} dot={subscription.status === "active"}>
                        {subscription.status}
                      </Chip>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-ink-muted">{formatDateTime(subscription.effective_from)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <BoundHint
            text={boundHint(data.campaign_subscriptions.length, DOSSIER_SECTION_CAP, "newest", "campaign subscriptions")}
          />
        </div>
      </SectionCard>

      <SectionCard
        title="Credits"
        blurb="The same ledger the organisation sees on its own account screen, read-only."
        badge={<Badge tone="success">Balance {formatCreditsWithUnit(data.credits.balance)}</Badge>}
      >
        {data.credits.ledger.rows.length === 0 ? (
          <EmptyState
            title="No credit movements in this page"
            body="The newest movements appear here; a brand-new organisation has none yet."
          />
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>When</TableHeaderCell>
                <TableHeaderCell>Action</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell className="text-right">Credits</TableHeaderCell>
                <TableHeaderCell>Note</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.credits.ledger.rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="whitespace-nowrap text-ink-muted">{formatDateTime(row.created_at)}</TableCell>
                  <TableCell className="font-medium">{humaniseAction(row.action)}</TableCell>
                  <TableCell>
                    <Badge tone={row.entry_type === "burn" ? "warning" : "success"}>
                      {humaniseAction(row.entry_type)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono">{formatCredits(row.credits_delta)}</TableCell>
                  <TableCell className="text-ink-muted">{row.memo ?? "None"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <BoundHint
          text={boundHint(data.credits.ledger.rows.length, data.credits.ledger.limit, "newest", "credit movements")}
        />
      </SectionCard>

      <SectionCard
        title="Usage"
        blurb="Month to date, the same grouped summary the organisation's own account screen reads."
      >
        <p className="mb-3 text-sm text-ink-muted">
          {formatDateTime(data.usage.window.from)} to {formatDateTime(data.usage.window.to)}
        </p>
        {data.usage.by_action.length === 0 ? (
          <EmptyState title="No usage recorded in this window" body="Once the organisation spends credits, the totals appear here." />
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Action</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell className="text-right">Times</TableHeaderCell>
                <TableHeaderCell className="text-right">Units</TableHeaderCell>
                <TableHeaderCell className="text-right">Credits</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.usage.by_action.map((row) => (
                <TableRow key={`${row.action ?? "none"}-${row.entry_type}`}>
                  <TableCell className="font-medium">{humaniseAction(row.action)}</TableCell>
                  <TableCell className="text-ink-muted">{humaniseAction(row.entry_type)}</TableCell>
                  <TableCell className="text-right">{row.entry_count.toLocaleString("en-GB")}</TableCell>
                  <TableCell className="text-right text-ink-muted">{formatCredits(row.unit_count_total)}</TableCell>
                  <TableCell className="text-right font-mono">{formatCredits(row.credits_delta_total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      <SectionCard title="Sites" blurb="The websites this organisation has connected.">
        {data.sites.length === 0 ? (
          <EmptyState title="No sites yet" body="Sites the organisation connects appear here." />
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Domain</TableHeaderCell>
                <TableHeaderCell>Pixel</TableHeaderCell>
                <TableHeaderCell>Connected</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.sites.map((site) => (
                <TableRow key={site.id}>
                  <TableCell className="font-medium">{site.domain}</TableCell>
                  <TableCell>
                    <Chip signal={site.pixel_status === "verified" ? "buy" : undefined} dot={site.pixel_status === "verified"}>
                      {site.pixel_status}
                    </Chip>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-ink-muted">{formatDateTime(site.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <BoundHint text={boundHint(data.sites.length, DOSSIER_SECTION_CAP, "oldest", "sites")} />
      </SectionCard>

      <SectionCard title="API keys" blurb="Metadata only - key values are never shown to anyone, including operators.">
        {data.api_keys.length === 0 ? (
          <EmptyState title="No API keys yet" body="Keys the organisation creates appear here." />
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Label</TableHeaderCell>
                <TableHeaderCell>Prefix</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Last used</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.api_keys.map((key) => (
                <TableRow key={key.id}>
                  <TableCell className="font-medium">{key.label ?? "None"}</TableCell>
                  <TableCell className="font-mono text-xs">{key.key_prefix}</TableCell>
                  <TableCell>
                    <Chip signal={statusSignal(key.status)} dot={key.status === "active"}>
                      {key.status}
                    </Chip>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-ink-muted">{formatDateTime(key.last_used_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <BoundHint text={boundHint(data.api_keys.length, DOSSIER_SECTION_CAP, "newest", "API keys")} />
      </SectionCard>

      <SectionCard
        title="Campaign bindings"
        blurb="The delivery coverage held on the platform for each campaign subscription, as last verified."
      >
        {data.campaign_bindings.length === 0 ? (
          <EmptyState
            title="No campaign bindings recorded yet"
            body="A binding appears once the campaign's delivery coverage is set on the access screen."
          />
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Campaign subscription</TableHeaderCell>
                <TableHeaderCell>Sync state</TableHeaderCell>
                <TableHeaderCell>Requested coverage</TableHeaderCell>
                <TableHeaderCell>Last verified</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.campaign_bindings.map((binding) => (
                <TableRow key={binding.campaign_subscription_id}>
                  <TableCell className="font-mono text-xs">{binding.campaign_subscription_id}</TableCell>
                  <TableCell>
                    <Chip signal={binding.sync_state === "verified" ? "buy" : binding.sync_state === "failed" ? "risk" : undefined}>
                      {binding.sync_state}
                    </Chip>
                  </TableCell>
                  <TableCell className="text-ink-muted">{scopeSummary(binding.requested_geo_scope)}</TableCell>
                  <TableCell className="whitespace-nowrap text-ink-muted">{formatDateTime(binding.last_verified_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <BoundHint text={boundHint(data.campaign_bindings.length, DOSSIER_SECTION_CAP, "first", "campaign bindings")} />
      </SectionCard>

      <SectionCard title="Audit history" blurb="Every recorded event for this organisation, newest first.">
        {data.audit.rows.length === 0 ? (
          <EmptyState title="No history yet" body="Events are recorded here from the organisation's first creation onwards." />
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>When</TableHeaderCell>
                <TableHeaderCell>Event</TableHeaderCell>
                <TableHeaderCell>Actor</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.audit.rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="whitespace-nowrap text-ink-muted">{formatDateTime(row.created_at)}</TableCell>
                  <TableCell className="font-medium">{humaniseAction(row.action)}</TableCell>
                  <TableCell className="text-ink-muted">{row.actor_type ?? "None"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <BoundHint text={boundHint(data.audit.rows.length, data.audit.limit, "newest", "events")} />
      </SectionCard>
    </div>
  );
}
